import React from 'react';
import PropTypes from 'prop-types';
import LayoutPlaceholderCard from '../../../../components/shared/LayoutPlaceholderCard';
import { getCardPlaceholderMeta } from '../../../../utils/getCardDisplayTitle';
import { getHospitalLayoutSkeletonVariant } from '../../../../utils/hospitalLayoutPlaceholderSkeleton';
import { getLayoutPreviewSharePanelProps } from '../../../../utils/layoutPreviewShareBadge';
import {
	arePreviewPropsEqual,
	DaysPreviewBody,
	FloorsPreviewBody,
	InsurancePreviewBody,
	NinetyDaysPreviewBody,
	resolveDynamicPreviewBody,
	ReturnsPreviewBody,
	SliderPreviewBody,
	UserListPreviewBody,
} from '../../../../components/shared/layoutPreview';

/**
 * Layout-mode preview renderer for the Community Transfer page.
 *
 * See `components/shared/layoutPreview/` for the shared primitives. This
 * file just maps each Community-Transfer-specific `card.component` value
 * to the matching shared body, or delegates to the dynamic resolver.
 *
 * Slider-style cards (`safeDischargeAssLiv`, `snfFacility`) reuse the
 * shared `SliderPreviewBody` — the real carousel skeletons mount
 * `react-slick` with `infinite: true`, which is too heavy to run under
 * a drag; the static silhouette communicates the same shape at a
 * fraction of the cost. See `previewBodies.js` for the full rationale.
 */
const PREVIEW_BY_COMPONENT = {
	insurance: InsurancePreviewBody,
	returns: ReturnsPreviewBody,
	// `sixtyDays` renders `NinetyDaysAnalysis` — same silhouette.
	sixtyDays: NinetyDaysPreviewBody,
	days: DaysPreviewBody,
	doctor: UserListPreviewBody,
	floors: FloorsPreviewBody,
	safeDischargeAssLiv: SliderPreviewBody,
	snfFacility: SliderPreviewBody,
};

const CommunityTransferCardLayoutPreview = ({ card, dynamicCards, sharedUsersByTabId }) => {
	const { title: placeholderTitle } = getCardPlaceholderMeta(card, {
		dynamicCards,
	});

	let PreviewBody = null;
	let previewBodyProps = {};
	if (card?.component === 'dynamic') {
		PreviewBody = resolveDynamicPreviewBody(card?.data);
		previewBodyProps = { role: 'generic', useGenericLabels: true };
	} else if (card?.component) {
		PreviewBody = PREVIEW_BY_COMPONENT[card.component] || null;
		if (card.component === 'doctor') {
			previewBodyProps = { role: 'doctor' };
		}
	}

	return (
		<LayoutPlaceholderCard
			variant="card"
			title={placeholderTitle}
			width={card?.width}
			sharePanel={getLayoutPreviewSharePanelProps(card, { sharedUsersByTabId })}
			skeletonVariant={getHospitalLayoutSkeletonVariant(card)}
			body={PreviewBody ? <PreviewBody {...previewBodyProps} /> : null}
		/>
	);
};

CommunityTransferCardLayoutPreview.propTypes = {
	card: PropTypes.shape({
		id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		component: PropTypes.string,
		type: PropTypes.string,
		width: PropTypes.number,
		data: PropTypes.object,
	}).isRequired,
	dynamicCards: PropTypes.array,
	sharedUsersByTabId: PropTypes.object,
};

CommunityTransferCardLayoutPreview.defaultProps = {
	dynamicCards: [],
	sharedUsersByTabId: undefined,
};

export default React.memo(CommunityTransferCardLayoutPreview, arePreviewPropsEqual);
